import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  FiAlertTriangle, FiArrowDown, FiArrowUp, FiBell, FiCalendar, FiCheckCircle,
  FiChevronDown, FiChevronLeft, FiChevronRight, FiExternalLink, FiFolder,
  FiGlobe, FiInbox, FiInfo, FiLink, FiPlus, FiRefreshCw, FiSearch, FiSend,
  FiSettings, FiTag, FiTrash2, FiUser, FiUserCheck, FiUsers, FiX,
} from 'react-icons/fi';
import BASE_URL from '../../../config/config';
import Loader from '../../../components/Loader';
import { cabecerasAuth, claves } from '../../../queries/cliente';
import i18n from '../../../i18n';
import 'react-toastify/dist/ReactToastify.css';

const ICONOS = { campana: FiBell, ticket: FiTag, info: FiInfo, alerta: FiAlertTriangle, usuario: FiUser };
const ICONOS_TIPO = { sistema: FiSettings, ticket: FiTag, proyecto: FiFolder, usuario: FiUser };
const ICONOS_AUDIENCIA = { todos: FiUsers, admins: FiUserCheck, tecnicos: FiUserCheck, admins_tecnicos: FiUsers, especifico: FiUser };
const TIPOS = ['sistema', 'ticket', 'proyecto', 'usuario'];
const PRIORIDADES = ['baja', 'normal', 'alta', 'urgente'];
const AUDIENCIAS = ['todos', 'admins', 'tecnicos', 'admins_tecnicos', 'especifico'];
const IDIOMAS = ['es', 'en'];
const VACIO = { es: { titulo: '', mensaje: '' }, en: { titulo: '', mensaje: '' } };

const datosExtra = (notificacion) => {
  if (!notificacion?.datos_adicionales) return {};
  if (typeof notificacion.datos_adicionales === 'object') return notificacion.datos_adicionales;
  try { return JSON.parse(notificacion.datos_adicionales); } catch (_) { return {}; }
};

const fechaCompleta = (fecha) => new Intl.DateTimeFormat(i18n.language, {
  day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
}).format(new Date(fecha));

const fechaRelativa = (fecha) => {
  const minutos = Math.floor((Date.now() - new Date(fecha).getTime()) / 60000);
  if (minutos < 1) return i18n.t('gestorNotificaciones.ahora');
  if (minutos < 60) return i18n.t('gestorNotificaciones.haceMin', { n: minutos });
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return i18n.t('gestorNotificaciones.haceHoras', { n: horas });
  const dias = Math.floor(horas / 24);
  if (dias < 30) return i18n.t('gestorNotificaciones.haceDias', { n: dias });
  return new Date(fecha).toLocaleDateString(i18n.language);
};

const NotificacionesManager = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = localStorage.getItem('AuthToken');
  const esAdmin = token ? jwtDecode(token).role === 'admin' : false;

  const [componiendo, setComponiendo] = useState(false);
  const [audiencia, setAudiencia] = useState('todos');
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState('');
  const [busquedaUsuario, setBusquedaUsuario] = useState('');
  const [mostrarUsuarios, setMostrarUsuarios] = useState(false);
  const [contenido, setContenido] = useState(VACIO);
  const [idiomaActivo, setIdiomaActivo] = useState('es');
  const [tipo, setTipo] = useState('sistema');
  const [prioridad, setPrioridad] = useState('normal');
  const [icono, setIcono] = useState('campana');
  const [link, setLink] = useState('');
  const [enviando, setEnviando] = useState(false);

  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todas');
  const [filtroAudiencia, setFiltroAudiencia] = useState('todas');
  const [filtroLectura, setFiltroLectura] = useState('todas');
  const [orden, setOrden] = useState('desc');
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [expandidas, setExpandidas] = useState([]);
  const [eliminando, setEliminando] = useState(false);
  const [pagina, setPagina] = useState(1);
  const porPagina = 15;

  useEffect(() => { if (!esAdmin) navigate('/'); }, [esAdmin, navigate]);
  useEffect(() => {
    if (!componiendo) return undefined;
    const cerrar = (event) => { if (event.key === 'Escape' && !enviando) setComponiendo(false); };
    document.addEventListener('keydown', cerrar);
    return () => document.removeEventListener('keydown', cerrar);
  }, [componiendo, enviando]);

  const [consultaNotificaciones, consultaUsuarios, consultaEstadisticas] = useQueries({
    queries: [
      {
        queryKey: claves.notificacionesAdmin(),
        queryFn: async () => (await axios.get(`${BASE_URL}/notificaciones-admin/todas?limite=500`, cabecerasAuth())).data.notificaciones || [],
        enabled: esAdmin,
      },
      {
        queryKey: claves.usuariosNotificables(),
        queryFn: async () => (await axios.get(`${BASE_URL}/notificaciones-admin/usuarios`, cabecerasAuth())).data.usuarios || [],
        enabled: esAdmin, staleTime: 5 * 60_000,
      },
      {
        queryKey: claves.estadisticasNotificaciones(),
        queryFn: async () => (await axios.get(`${BASE_URL}/notificaciones-admin/estadisticas`, cabecerasAuth())).data.estadisticas,
        enabled: esAdmin,
      },
    ],
  });

  useEffect(() => {
    if (consultaNotificaciones.isError || consultaUsuarios.isError || consultaEstadisticas.isError) {
      toast.error(t('gestorNotificaciones.errorCargarDatos'));
    }
  }, [consultaNotificaciones.isError, consultaUsuarios.isError, consultaEstadisticas.isError, t]);

  const notificaciones = useMemo(() => consultaNotificaciones.data || [], [consultaNotificaciones.data]);
  const usuarios = useMemo(() => consultaUsuarios.data || [], [consultaUsuarios.data]);
  const estadisticas = consultaEstadisticas.data;
  const refrescando = consultaNotificaciones.isFetching && !consultaNotificaciones.isPending;

  const recargar = () => {
    setSeleccionadas([]);
    queryClient.invalidateQueries({ queryKey: claves.notificacionesAdmin() });
    queryClient.invalidateQueries({ queryKey: claves.estadisticasNotificaciones() });
  };

  const limpiarCompositor = () => {
    setAudiencia('todos'); setUsuarioSeleccionado(''); setBusquedaUsuario('');
    setContenido(VACIO); setIdiomaActivo('es'); setTipo('sistema');
    setPrioridad('normal'); setIcono('campana'); setLink('');
  };

  const actualizarContenido = (campo, valor) => setContenido((actual) => ({
    ...actual, [idiomaActivo]: { ...actual[idiomaActivo], [campo]: valor },
  }));

  const destinatariosEstimados = useMemo(() => {
    if (audiencia === 'especifico') return usuarioSeleccionado ? 1 : 0;
    if (audiencia === 'admins') return usuarios.filter((u) => u.role === 'admin').length;
    if (audiencia === 'tecnicos') return usuarios.filter((u) => u.role === 'tecnico').length;
    if (audiencia === 'admins_tecnicos') return usuarios.filter((u) => u.role === 'admin' || u.role === 'tecnico').length;
    return usuarios.length;
  }, [audiencia, usuarioSeleccionado, usuarios]);

  const usuariosFiltrados = useMemo(() => {
    const texto = busquedaUsuario.trim().toLowerCase();
    if (!texto) return [];
    return usuarios.filter((u) => `${u.nombre} ${u.apellidos} ${u.usuario} ${u.email}`.toLowerCase().includes(texto)).slice(0, 8);
  }, [busquedaUsuario, usuarios]);

  const enviar = async (event) => {
    event.preventDefault();
    if (audiencia === 'especifico' && !usuarioSeleccionado) {
      toast.error(t('gestorNotificaciones.seleccionaUsuarioDestinatario')); return;
    }
    if (IDIOMAS.some((idioma) => !contenido[idioma].titulo.trim() || !contenido[idioma].mensaje.trim())) {
      toast.error(t('gestorNotificaciones.completaIdiomas')); return;
    }
    setEnviando(true);
    try {
      const { data } = await axios.post(`${BASE_URL}/notificaciones`, {
        audiencia,
        usuario_destinatario: audiencia === 'especifico' ? usuarioSeleccionado : null,
        contenido,
        tipo, prioridad, icono, link: link.trim() || null,
      }, cabecerasAuth());
      toast.success(t('gestorNotificaciones.envioEncolado', {
        destinatarios: data.destinatarios,
        correos: data.correosEncolados,
      }));
      limpiarCompositor(); setComponiendo(false); recargar();
    } catch (error) {
      toast.error(error.response?.data?.message || t('gestorNotificaciones.errorEnviar'));
    } finally { setEnviando(false); }
  };

  const filtradas = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return [...notificaciones].filter((n) => {
      const coincideTexto = !texto || `${n.titulo} ${n.mensaje} ${n.usuario_destinatario || ''}`.toLowerCase().includes(texto);
      const coincideTipo = filtroTipo === 'todas' || n.tipo === filtroTipo;
      const coincideAudiencia = filtroAudiencia === 'todas' || n.audiencia === filtroAudiencia;
      const vistas = Number(n.estadisticas?.vistas || 0);
      const total = Number(n.estadisticas?.destinatarios || 0);
      const completa = total > 0 && vistas >= total;
      const coincideLectura = filtroLectura === 'todas' || (filtroLectura === 'completas' ? completa : !completa);
      return coincideTexto && coincideTipo && coincideAudiencia && coincideLectura;
    }).sort((a, b) => (orden === 'desc' ? 1 : -1) * (new Date(b.createdAt) - new Date(a.createdAt)));
  }, [notificaciones, busqueda, filtroTipo, filtroAudiencia, filtroLectura, orden]);

  useEffect(() => { setPagina(1); setSeleccionadas([]); }, [busqueda, filtroTipo, filtroAudiencia, filtroLectura, orden]);
  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / porPagina));
  const actuales = filtradas.slice((pagina - 1) * porPagina, pagina * porPagina);
  useEffect(() => { if (pagina > totalPaginas) setPagina(totalPaginas); }, [pagina, totalPaginas]);

  const eliminarUna = async (id) => {
    if (!window.confirm(t('gestorNotificaciones.confirmarEliminarUna'))) return;
    try {
      await axios.delete(`${BASE_URL}/notificaciones/${id}`, cabecerasAuth());
      toast.success(t('gestorNotificaciones.notificacionEliminada')); recargar();
    } catch (_) { toast.error(t('gestorNotificaciones.errorEliminar')); }
  };

  const eliminarSeleccionadas = async () => {
    if (!seleccionadas.length || !window.confirm(t('gestorNotificaciones.confirmarEliminarVarias', { count: seleccionadas.length }))) return;
    setEliminando(true);
    const resultados = await Promise.allSettled(seleccionadas.map((id) => axios.delete(`${BASE_URL}/notificaciones/${id}`, cabecerasAuth())));
    const fallidas = resultados.filter((r) => r.status === 'rejected').length;
    if (fallidas) toast.error(t('gestorNotificaciones.eliminacionParcial', { eliminadas: resultados.length - fallidas, fallidas }));
    else toast.success(t('gestorNotificaciones.notificacionesEliminadas'));
    setEliminando(false); recargar();
  };

  const todasPagina = actuales.length > 0 && actuales.every((n) => seleccionadas.includes(n.id));
  const seleccionarPagina = () => setSeleccionadas((prev) => todasPagina
    ? prev.filter((id) => !actuales.some((n) => n.id === id))
    : [...new Set([...prev, ...actuales.map((n) => n.id)])]);

  const abrirEnlace = (notificacion) => {
    if (!notificacion.link) return;
    if (notificacion.link.startsWith('/')) navigate(notificacion.link);
    else window.open(notificacion.link, '_blank', 'noopener,noreferrer');
  };

  if (consultaNotificaciones.isPending) return <div className="loader-fullscreen"><Loader /></div>;

  return (
    <main className="site-width">
      <ToastContainer position="bottom-right" autoClose={3500} />
      <div className="gn-page gn-page--pro">
        <header className="gn-header">
          <div className="pagina-cabecera">
            <span className="gn-eyebrow"><FiBell /> {t('gestorNotificaciones.centroComunicaciones')}</span>
            <h2>{t('gestorNotificaciones.titulo')}</h2>
            <p>{t('gestorNotificaciones.subtituloProfesional')}</p>
          </div>
          <div className="gn-header-acciones">
            <button type="button" className="gn-boton-icono" onClick={recargar} disabled={refrescando} aria-label={t('gestorNotificaciones.actualizar')}>
              <FiRefreshCw className={refrescando ? 'gn-girando' : ''} />
            </button>
            <button type="button" className="boton-primario gn-boton-nueva" onClick={() => setComponiendo(true)}>
              <FiPlus /> {t('gestorNotificaciones.crearEnvio')}
            </button>
          </div>
        </header>

        {estadisticas && (
          <section className="gn-stats" aria-label={t('gestorNotificaciones.resumen')}>
            {[
              [FiBell, estadisticas.totalNotificaciones, t('gestorNotificaciones.enviosHistoricos'), t('gestorNotificaciones.registroCompleto')],
              [FiUsers, estadisticas.notificacionesMasivas, t('gestorNotificaciones.enviosSegmentados'), t('gestorNotificaciones.audienciasMultiples')],
              [FiInbox, estadisticas.individualesNoLeidas, t('gestorNotificaciones.pendientesLectura'), t('gestorNotificaciones.enviosIndividuales')],
              [FiCheckCircle, `${estadisticas.porcentajeLeidasIndividuales}%`, t('gestorNotificaciones.tasaLectura'), t('gestorNotificaciones.individuales')],
            ].map(([Icono, valor, label, sub]) => (
              <article className="gn-stat-card" key={label}><span className="gn-stat-icono"><Icono /></span><div><p className="gn-stat-valor">{valor}</p><p className="gn-stat-label">{label}</p><p className="gn-stat-sub">{sub}</p></div></article>
            ))}
          </section>
        )}

        <section className="gn-gestion">
          <div className="gn-gestion-titulo">
            <div><h3>{t('gestorNotificaciones.historialEnvios')}</h3><p>{t('gestorNotificaciones.historialAyuda')}</p></div>
            <span className="gn-total-registros">{filtradas.length}</span>
          </div>

          <div className="gn-toolbar">
            {seleccionadas.length ? (
              <div className="gn-toolbar-seleccion">
                <span className="gn-toolbar-seleccion-info"><FiCheckCircle /> {t('gestorNotificaciones.seleccionadas', { count: seleccionadas.length })}</span>
                <button type="button" className="gn-boton-cancelar" onClick={() => setSeleccionadas([])}>{t('comun.cancelar')}</button>
                <button type="button" className="gn-boton-peligro" onClick={eliminarSeleccionadas} disabled={eliminando}><FiTrash2 /> {t('gestorNotificaciones.eliminarSeleccionadas')}</button>
              </div>
            ) : (
              <>
                <label className="gn-campo-icono gn-buscador"><FiSearch className="gn-icono-input" /><span className="sr-only">{t('comun.buscar')}</span><input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder={t('gestorNotificaciones.buscarPorTituloMensaje')} className="gn-input-con-icono" /></label>
                <select aria-label={t('gestorNotificaciones.tipoNotificacion')} value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>{<option value="todas">{t('gestorNotificaciones.todosLosTipos')}</option>}{TIPOS.map((v) => <option key={v} value={v}>{t(`gestorNotificaciones.tipos.${v}`)}</option>)}</select>
                <select aria-label={t('gestorNotificaciones.filtroAudiencia')} value={filtroAudiencia} onChange={(e) => setFiltroAudiencia(e.target.value)}><option value="todas">{t('gestorNotificaciones.todasAudiencias')}</option>{AUDIENCIAS.map((v) => <option key={v} value={v}>{t(`gestorNotificaciones.destinatarios.${v}`)}</option>)}</select>
                <select aria-label={t('gestorNotificaciones.filtroLectura')} value={filtroLectura} onChange={(e) => setFiltroLectura(e.target.value)}><option value="todas">{t('gestorNotificaciones.todasLecturas')}</option><option value="pendientes">{t('gestorNotificaciones.conLecturasPendientes')}</option><option value="completas">{t('gestorNotificaciones.lecturaCompleta')}</option></select>
                <button type="button" className="gn-boton-icono" onClick={() => setOrden((v) => v === 'desc' ? 'asc' : 'desc')} aria-label={t('gestorNotificaciones.cambiarOrden')}>{orden === 'desc' ? <FiArrowDown /> : <FiArrowUp />}</button>
              </>
            )}
          </div>

          <div className="gn-lista-cabecera">
            <label className="gn-checkbox-todos"><input type="checkbox" checked={todasPagina} onChange={seleccionarPagina} /> {t('gestorNotificaciones.seleccionarPagina')}</label>
            <p>{t('gestorNotificaciones.rangoResultados', { desde: filtradas.length ? (pagina - 1) * porPagina + 1 : 0, hasta: Math.min(pagina * porPagina, filtradas.length), total: filtradas.length })}</p>
          </div>

          {actuales.length ? <div className="gn-panel">
            {actuales.map((notificacion) => {
              const expandida = expandidas.includes(notificacion.id);
              const Icono = ICONOS[notificacion.icono] || ICONOS_TIPO[notificacion.tipo] || FiBell;
              const vistas = Number(notificacion.estadisticas?.vistas || 0);
              const total = Number(notificacion.estadisticas?.destinatarios || 0);
              const porcentaje = total ? Math.round((vistas / total) * 100) : 0;
              return <article className={`gn-row ${expandida ? 'expandida' : ''}`} key={notificacion.id}>
                <div className="gn-row-compacta">
                  <input type="checkbox" className="gn-card-checkbox" checked={seleccionadas.includes(notificacion.id)} onChange={() => setSeleccionadas((p) => p.includes(notificacion.id) ? p.filter((id) => id !== notificacion.id) : [...p, notificacion.id])} aria-label={t('gestorNotificaciones.seleccionarNotificacion')} />
                  <span className={`gn-card-icono tipo-${notificacion.tipo}`}><Icono /></span>
                  <button type="button" className="gn-row-abrir" onClick={() => setExpandidas((p) => p.includes(notificacion.id) ? p.filter((id) => id !== notificacion.id) : [...p, notificacion.id])} aria-expanded={expandida}>
                    <span className="gn-row-titulo"><strong>{notificacion.titulo}</strong><small>{notificacion.mensaje}</small></span>
                    <span className="gn-row-etiquetas"><span className={`etiqueta-prioridad prioridad-${notificacion.prioridad}`}>{t(`gestorNotificaciones.prioridades.${notificacion.prioridad}`)}</span><span className="etiqueta-masiva">{t(`gestorNotificaciones.destinatarios.${notificacion.audiencia || 'todos'}`)}</span></span>
                    <span className="gn-row-lectura"><b>{vistas}/{total}</b><small>{t('gestorNotificaciones.lecturas')}</small></span>
                    <span className="gn-row-fecha">{fechaRelativa(notificacion.createdAt)}</span>
                    <FiChevronDown className="gn-row-chevron" />
                  </button>
                  <button type="button" className="boton-eliminar" onClick={() => eliminarUna(notificacion.id)} aria-label={t('gestorNotificaciones.eliminarNotificacion')}><FiTrash2 /></button>
                </div>
                {expandida && <div className="gn-row-detalle">
                  <div className="gn-progreso"><span style={{ width: `${porcentaje}%` }} /></div>
                  <div className="gn-detalle-grid">
                    <div><span>{t('gestorNotificaciones.alcance')}</span><strong>{total} {t('gestorNotificaciones.destinatariosLabel')}</strong></div>
                    <div><span>{t('gestorNotificaciones.lectura')}</span><strong>{porcentaje}%</strong></div>
                    <div><span>{t('gestorNotificaciones.enviadoPor')}</span><strong>@{notificacion.enviado_por || t('gestorNotificaciones.sistema')}</strong></div>
                    <div><span>{t('gestorNotificaciones.fechaEnvio')}</span><strong>{fechaCompleta(notificacion.createdAt)}</strong></div>
                  </div>
                  {notificacion.link && <button type="button" className="gn-row-enlace" onClick={() => abrirEnlace(notificacion)}><FiLink /> {notificacion.link} <FiExternalLink /></button>}
                </div>}
              </article>;
            })}
          </div> : <div className="gn-vacio"><FiInbox /><h3>{t('gestorNotificaciones.sinNotificacionesEncontradas')}</h3><p>{t('gestorNotificaciones.ajustaFiltros')}</p></div>}

          {totalPaginas > 1 && <nav className="paginacion" aria-label={t('gestorNotificaciones.paginacion')}><button type="button" className="boton-paginacion" disabled={pagina === 1} onClick={() => setPagina((p) => p - 1)}><FiChevronLeft /> {t('gestorNotificaciones.anterior')}</button><span>{t('gestorNotificaciones.paginaDe', { actual: pagina, total: totalPaginas })}</span><button type="button" className="boton-paginacion" disabled={pagina === totalPaginas} onClick={() => setPagina((p) => p + 1)}>{t('gestorNotificaciones.siguiente')} <FiChevronRight /></button></nav>}
        </section>
      </div>

      {componiendo && <div className="gn-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !enviando) setComponiendo(false); }}>
        <section className="gn-modal gn-compositor" role="dialog" aria-modal="true" aria-labelledby="gn-compositor-titulo">
          <header className="gn-modal-header"><div><span className="gn-eyebrow"><FiSend /> {t('gestorNotificaciones.nuevoEnvio')}</span><h3 id="gn-compositor-titulo">{t('gestorNotificaciones.redactarNotificacion')}</h3></div><button type="button" className="gn-boton-cerrar" onClick={() => setComponiendo(false)} aria-label={t('comun.cerrar')}><FiX /></button></header>
          <form onSubmit={enviar} className="gn-modal-form">
            <section className="gn-form-bloque"><div className="gn-form-cabecera"><div><span>01</span><h4>{t('gestorNotificaciones.audiencia')}</h4></div><p>{t('gestorNotificaciones.audienciaAyuda')}</p></div>
              <div className="gn-audiencias" role="radiogroup">
                {AUDIENCIAS.map((valor) => { const Icono = ICONOS_AUDIENCIA[valor]; return <button type="button" role="radio" aria-checked={audiencia === valor} className={`gn-audiencia ${audiencia === valor ? 'activa' : ''}`} key={valor} onClick={() => { setAudiencia(valor); if (valor !== 'especifico') { setUsuarioSeleccionado(''); setBusquedaUsuario(''); } }}><Icono /><span><strong>{t(`gestorNotificaciones.destinatarios.${valor}`)}</strong><small>{t(`gestorNotificaciones.audienciaDesc.${valor}`)}</small></span></button>; })}
              </div>
              {audiencia === 'especifico' && <div className="selector-usuario-busqueda"><label htmlFor="gn-usuario">{t('gestorNotificaciones.usuarioDestinatario')}</label><div className="gn-campo-icono"><FiSearch className="gn-icono-input" /><input id="gn-usuario" value={busquedaUsuario} onChange={(e) => { setBusquedaUsuario(e.target.value); setUsuarioSeleccionado(''); setMostrarUsuarios(true); }} onFocus={() => setMostrarUsuarios(true)} placeholder={t('gestorNotificaciones.buscarUsuarioPlaceholder')} className="gn-input-con-icono" /></div>{mostrarUsuarios && usuariosFiltrados.length > 0 && <div className="lista-usuarios-desplegable" role="listbox">{usuariosFiltrados.map((u) => <button type="button" role="option" key={u.usuario} className="item-usuario-desplegable" onClick={() => { setUsuarioSeleccionado(u.usuario); setBusquedaUsuario(`${u.nombre} ${u.apellidos} (@${u.usuario})`); setMostrarUsuarios(false); }}><span><strong>{u.nombre} {u.apellidos}</strong><small>@{u.usuario} · {u.email}</small></span><em>{String(u.idioma || 'es').toUpperCase()}</em></button>)}</div>}</div>}
              <div className="gn-alcance"><FiUsers /><span><strong>{destinatariosEstimados}</strong> {t('gestorNotificaciones.destinatariosEstimados')}</span></div>
            </section>

            <section className="gn-form-bloque"><div className="gn-form-cabecera"><div><span>02</span><h4>{t('gestorNotificaciones.contenido')}</h4></div><p>{t('gestorNotificaciones.contenidoAyuda')}</p></div>
              <div className="gn-idiomas" role="tablist">{IDIOMAS.map((idioma) => <button type="button" role="tab" aria-selected={idiomaActivo === idioma} className={idiomaActivo === idioma ? 'activo' : ''} key={idioma} onClick={() => setIdiomaActivo(idioma)}><FiGlobe /> {t(`gestorNotificaciones.idiomas.${idioma}`)} {contenido[idioma].titulo && contenido[idioma].mensaje ? <FiCheckCircle /> : null}</button>)}</div>
              <div className="gn-contenido-grid">
                <div className="gn-campos-contenido"><label htmlFor="gn-titulo">{t('gestorNotificaciones.tituloObligatorio')}</label><input id="gn-titulo" maxLength={255} value={contenido[idiomaActivo].titulo} onChange={(e) => actualizarContenido('titulo', e.target.value)} placeholder={t('gestorNotificaciones.placeholderTitulo')} /><label htmlFor="gn-mensaje">{t('gestorNotificaciones.mensajeObligatorio')}</label><textarea id="gn-mensaje" rows={6} value={contenido[idiomaActivo].mensaje} onChange={(e) => actualizarContenido('mensaje', e.target.value)} placeholder={t('gestorNotificaciones.placeholderMensaje')} /></div>
                <aside className="gn-preview"><span>{t('gestorNotificaciones.vistaPrevia')}</span><div className={`gn-preview-card prioridad-${prioridad}`}><div className="gn-preview-icono">{React.createElement(ICONOS[icono] || FiBell)}</div><div><strong>{contenido[idiomaActivo].titulo || t('gestorNotificaciones.previewTitulo')}</strong><p>{contenido[idiomaActivo].mensaje || t('gestorNotificaciones.previewMensaje')}</p><small>{t('gestorNotificaciones.ahora')}</small></div></div></aside>
              </div>
            </section>

            <section className="gn-form-bloque"><div className="gn-form-cabecera"><div><span>03</span><h4>{t('gestorNotificaciones.configuracion')}</h4></div><p>{t('gestorNotificaciones.configuracionAyuda')}</p></div><div className="gn-config-grid"><label>{t('gestorNotificaciones.tipoNotificacion')}<select value={tipo} onChange={(e) => setTipo(e.target.value)}>{TIPOS.map((v) => <option value={v} key={v}>{t(`gestorNotificaciones.tipos.${v}`)}</option>)}</select></label><label>{t('gestorNotificaciones.prioridad')}<select value={prioridad} onChange={(e) => setPrioridad(e.target.value)}>{PRIORIDADES.map((v) => <option value={v} key={v}>{t(`gestorNotificaciones.prioridades.${v}`)}</option>)}</select></label><label>{t('gestorNotificaciones.icono')}<select value={icono} onChange={(e) => setIcono(e.target.value)}>{Object.keys(ICONOS).map((v) => <option value={v} key={v}>{t(`gestorNotificaciones.iconos.${v}`)}</option>)}</select></label></div><label htmlFor="gn-link">{t('gestorNotificaciones.enlaceOpcional')}</label><div className="gn-campo-icono"><FiLink className="gn-icono-input" /><input id="gn-link" className="gn-input-con-icono" value={link} onChange={(e) => setLink(e.target.value)} placeholder={t('gestorNotificaciones.placeholderEnlace')} /></div></section>
            <footer className="gn-compositor-footer"><p><FiInfo /> {t('gestorNotificaciones.envioIdiomaNota')}</p><div><button type="button" className="gn-boton-cancelar" onClick={() => setComponiendo(false)}>{t('comun.cancelar')}</button><button type="submit" className="boton-primario" disabled={enviando || destinatariosEstimados === 0}><FiSend /> {enviando ? t('gestorNotificaciones.enviando') : t('gestorNotificaciones.enviarA', { count: destinatariosEstimados })}</button></div></footer>
          </form>
        </section>
      </div>}
    </main>
  );
};

export default NotificacionesManager;
